import {
  ChatConnectOptions,
  ChatMessage,
  JoinRoomPayload,
  JoinRoomResponse,
  RoomConnection,
  ConnectRoomOptions,
  RoomPresenceEvent,
  RoomStatus,
  ChatEnvelope,
  ChatJsonCodec,
  RoomStateRequest,
} from '../types/chat.types';
type SocketLike = any;

const DEFAULT_NAMESPACE = '/api/v2/chat';
const DEFAULT_PATH = '/api/v2/chat/socket.io';
const DEFAULT_TIMEOUT = 5000;

const waitForConnect = (socket: SocketLike, timeoutMs: number) =>
  new Promise<void>((resolve, reject) => {
    if (socket.connected) {
      resolve();
      return;
    }
    const timer = setTimeout(() => reject(new Error('Socket connect timeout')), timeoutMs);
    socket.once('connect', () => {
      clearTimeout(timer);
      resolve();
    });
    socket.once('connect_error', (err: Error) => {
      clearTimeout(timer);
      reject(err);
    });
  });

const emitWithAck = <Req, Res>(socket: SocketLike, event: string, payload: Req, timeoutMs: number) =>
  new Promise<Res>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timeout waiting for ack on ${event}`)), timeoutMs + 200);
    const emitter = typeof socket.timeout === 'function' ? socket.timeout(timeoutMs) : socket;
    emitter.emit(event, payload, function (err: any, data: any) {
      clearTimeout(timer);
      if (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      if (arguments.length >= 2) {
        resolve(data ?? err);
        return;
      }
      resolve(err as Res);
    });
  });

const makeClientMsgId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

const normalizeBase = (base: string) => base.replace(/\/+$/, '');

export class ChatClient {
  private socket?: SocketLike;
  private socketBase?: string;

  constructor(private baseUrl: string) {
    this.baseUrl = normalizeBase(baseUrl);
  }

  /**
   * Connects to the Socket.IO chat namespace with polling fallback.
   */
  async connect(options: ChatConnectOptions = {}, baseOverride?: string): Promise<SocketLike> {
    const effectiveBase = normalizeBase(baseOverride ?? this.baseUrl);

    if (this.socket?.connected && this.socketBase === effectiveBase) {
      return this.socket;
    }

    if (this.socket && this.socketBase !== effectiveBase) {
      this.disconnect();
    }

    const base = effectiveBase;
    const apiRootStripped = base.replace(/\/api\/v2$/, '');
    const { io } = await import('socket.io-client');
    const namespace = options.namespace ?? DEFAULT_NAMESPACE;
    const url = `${apiRootStripped}${namespace}`;

    this.socket = io(url, {
      path: options.path ?? DEFAULT_PATH,
      transports: options.transports ?? ['polling', 'websocket'],
      timeout: options.timeout ?? DEFAULT_TIMEOUT,
      auth: options.token ? { token: options.token } : undefined,
      extraHeaders: options.headers,
      reconnection: (options as any).reconnection,
      reconnectionAttempts: (options as any).reconnectionAttempts,
      reconnectionDelay: (options as any).reconnectionDelay,
      reconnectionDelayMax: (options as any).reconnectionDelayMax,
    });

    this.socketBase = effectiveBase;
    await waitForConnect(this.socket, options.timeout ?? DEFAULT_TIMEOUT);
    return this.socket;
  }

  /**
   * Disconnects from the chat namespace.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = undefined;
      this.socketBase = undefined;
    }
  }

  /**
   * Joins a room and receives history back.
   */
  async joinRoom(payload: JoinRoomPayload, options?: ChatConnectOptions, baseOverride?: string): Promise<JoinRoomResponse> {
    const socket = await this.connect(options, baseOverride);
    return emitWithAck(socket, 'room:join', payload, options?.timeout ?? DEFAULT_TIMEOUT);
  }

  /**
   * Leaves a room.
   */
  async leaveRoom(
    roomId: string,
    options?: ChatConnectOptions,
    baseOverride?: string,
  ): Promise<{ roomId: string; left: boolean }> {
    const socket = await this.connect(options, baseOverride);
    return emitWithAck(socket, 'room:leave', { roomId }, options?.timeout ?? DEFAULT_TIMEOUT);
  }

  /**
   * Sends a message to a room. Returns the server-authored ChatMessage.
   */
  async sendMessage(
    roomId: string,
    text: string,
    clientMsgId?: string,
    options?: ChatConnectOptions,
    baseOverride?: string,
  ): Promise<ChatMessage> {
    const socket = await this.connect(options, baseOverride);
    return emitWithAck(socket, 'message:send', { roomId, text, clientMsgId }, options?.timeout ?? DEFAULT_TIMEOUT);
  }

  /**
   * Subscribe to new messages. Returns an unsubscribe function.
   */
  async onMessage(
    handler: (message: ChatMessage) => void,
    options?: ChatConnectOptions,
  ): Promise<() => void> {
    const socket = await this.connect(options);
    socket.on('message:new', handler);
    return () => socket.off('message:new', handler);
  }

  /**
   * Subscribe to presence events (join/leave). Returns an unsubscribe function.
   */
  async onPresence(
    handler: (presence: { roomId: string; type: 'join' | 'leave'; socketId: string }) => void,
    options?: ChatConnectOptions,
  ): Promise<() => void> {
    const socket = await this.connect(options);
    socket.on('room:presence', handler);
    return () => socket.off('room:presence', handler);
  }

  /**
   * High-level helper that connects, joins, replays history, wires presence, and manages reconnect/resend.
   */
  async connectRoom<TEvents = Record<string, any>>(options: ConnectRoomOptions<TEvents>): Promise<RoomConnection<TEvents>> {
    const {
      roomId,
      clientId,
      since,
      reconnect,
      codec,
      presence = true,
      bootstrap,
      ...connectOpts
    } = options;

    const reconnectOptions = reconnect ?? { enabled: true };
    const socketConnectOpts: ChatConnectOptions & Record<string, any> = { ...connectOpts };
    if (reconnectOptions.enabled === false) {
      socketConnectOpts.reconnection = false;
    } else {
      socketConnectOpts.reconnection = true;
      if (reconnectOptions.maxAttempts != null) {
        socketConnectOpts.reconnectionAttempts = reconnectOptions.maxAttempts;
      }
      if (reconnectOptions.attemptTimeoutMs != null) {
        socketConnectOpts.reconnectionDelay = reconnectOptions.attemptTimeoutMs;
        socketConnectOpts.reconnectionDelayMax = reconnectOptions.attemptTimeoutMs;
      }
    }

    const effectiveBase = this.baseUrl;

    const socket = await this.connect(socketConnectOpts, effectiveBase);
    let currentStatus: RoomStatus = { status: 'connected' };
    let currentCursor = since;
    let joined = false;
    const seenMsgIds = new Set<string>();
    const pending = new Map<string, { roomId: string; text: string; clientMsgId: string }>();

    const messageHandlers = new Set<(msg: ChatMessage, meta: { isHistory: boolean }) => void>();
    const typedHandlers = new Map<string, Set<(payload: any, envelope: ChatEnvelope) => void>>();
    const presenceHandlers = new Set<(presence: RoomPresenceEvent) => void>();
    const statusHandlers = new Set<(status: RoomStatus) => void>();
    const stateProviders = new Set<(req: RoomStateRequest) => Promise<any>>();

    const updateStatus = (status: RoomStatus) => {
      currentStatus = status;
      statusHandlers.forEach(cb => cb(status));
    };

    const socketListeners: Array<{ event: string; handler: (...args: any[]) => void }> = [];
    const addSocketListener = (event: string, handler: (...args: any[]) => void) => {
      socket.on(event, handler);
      socketListeners.push({ event, handler });
    };
    const removeSocketListeners = () => {
      socketListeners.forEach(({ event, handler }) => socket.off(event, handler));
      socketListeners.length = 0;
    };

    const processMessage = (msg: ChatMessage, isHistory: boolean) => {
      if (seenMsgIds.has(msg.msgId)) return;
      seenMsgIds.add(msg.msgId);
      currentCursor = { seq: msg.seq };
      messageHandlers.forEach(cb => cb(msg, { isHistory }));

      if (codec) {
        const decoded = codec.decode(msg.text);
        if (decoded.ok) {
          const envelope = decoded.envelope;
          const handlersForType = typedHandlers.get(envelope.type);
          if (handlersForType) {
            handlersForType.forEach(cb => cb(envelope.payload, envelope));
          }
          if (msg.clientMsgId && pending.has(msg.clientMsgId)) {
            pending.delete(msg.clientMsgId);
          }
        }
      } else if (msg.clientMsgId && pending.has(msg.clientMsgId)) {
        pending.delete(msg.clientMsgId);
      }
    };

    const processPresence = (presenceEvent: RoomPresenceEvent) => {
      if (presenceEvent.roomId !== roomId) return;
      presenceHandlers.forEach(cb => cb(presenceEvent));
    };

    const resendPending = async () => {
      for (const [, value] of pending) {
        try {
          const sent = await this.sendMessage(value.roomId, value.text, value.clientMsgId, connectOpts, effectiveBase);
          pending.delete(sent.clientMsgId ?? value.clientMsgId);
        } catch {
          // keep pending if resend fails
        }
      }
      if (pending.size > 0) {
        statusHandlers.forEach(cb => cb({ status: currentStatus.status, error: currentStatus.error }));
      }
    };

    const messageListener = (msg: ChatMessage) => {
      if (msg.roomId !== roomId) return;
      processMessage(msg, false);
    };
    addSocketListener('message:new', messageListener);

    if (presence) {
      addSocketListener('room:presence', processPresence);
    }

    addSocketListener('disconnect', (reason: string) => {
      updateStatus({ status: 'disconnected', error: new Error(reason) });
    });

    addSocketListener('reconnect_attempt', () => updateStatus({ status: 'reconnecting' }));
    addSocketListener('reconnect', async () => {
      updateStatus({ status: 'reconnecting' });
      try {
        const joinResult = await this.joinRoom({ roomId, clientId, since: currentCursor }, connectOpts, effectiveBase);
        joinResult.history.forEach(msg => processMessage(msg, true));
        joined = true;
        updateStatus({ status: 'joined' });
        await resendPending();
        if (bootstrap?.autoRequest) {
          await requestState({ topic: bootstrap.topic, timeoutMs: bootstrap.timeoutMs });
        }
      } catch (err: any) {
        updateStatus({ status: 'error', error: err instanceof Error ? err : new Error(String(err)) });
      }
    });

    updateStatus({ status: 'connecting' });
    const joinResult = await this.joinRoom({ roomId, clientId, since }, connectOpts, effectiveBase);
    joinResult.history.forEach(msg => processMessage(msg, true));
    joined = true;
    updateStatus({ status: 'joined' });
    if (bootstrap?.autoRequest) {
      await requestState({ topic: bootstrap.topic, timeoutMs: bootstrap.timeoutMs });
    }

    const sendText = async (text: string, clientMsgId?: string): Promise<ChatMessage> => {
      const msgId = clientMsgId ?? makeClientMsgId();
      try {
        const sent = await this.sendMessage(roomId, text, msgId, connectOpts, effectiveBase);
        pending.delete(msgId);
        return sent;
      } catch (err) {
        pending.set(msgId, { roomId, text, clientMsgId: msgId });
        throw err;
      }
    };

    const send = async <K extends keyof TEvents & string>(type: K, payload: TEvents[K]) => {
      if (!codec) {
        throw new Error('No codec provided for typed send');
      }
      const { text, correlationId } = codec.encode(type, payload);
      return sendText(text, correlationId);
    };

    const onMessage = (handler: (msg: ChatMessage, meta: { isHistory: boolean }) => void) => {
      messageHandlers.add(handler);
      return () => messageHandlers.delete(handler);
    };

    const onTyped = <K extends keyof TEvents & string>(
      type: K,
      handler: (payload: TEvents[K], envelope: ChatEnvelope<TEvents[K]>) => void,
    ) => {
      const set = typedHandlers.get(type) ?? new Set();
      set.add(handler as any);
      typedHandlers.set(type, set);
      return () => {
        const current = typedHandlers.get(type);
        if (current) {
          current.delete(handler as any);
          if (current.size === 0) {
            typedHandlers.delete(type);
          }
        }
      };
    };

    const onPresence = (handler: (presence: RoomPresenceEvent) => void) => {
      presenceHandlers.add(handler);
      return () => presenceHandlers.delete(handler);
    };

    const onStatus = (handler: (status: RoomStatus) => void) => {
      statusHandlers.add(handler);
      handler(currentStatus);
      return () => statusHandlers.delete(handler);
    };

    async function requestState<TState = any>(opts?: { topic?: string; timeoutMs?: number }): Promise<TState | null> {
      const requestId = makeClientMsgId();
      const timeoutMs = opts?.timeoutMs ?? 5000;
      return new Promise<TState | null>((resolve) => {
        const onResponse = (response: { roomId: string; requestId: string; payload: any }) => {
          if (response.roomId !== roomId || response.requestId !== requestId) return;
          clearTimeout(timer);
          socket.off('room:state:response', onResponse);
          resolve(response.payload as TState);
        };

        const timer = setTimeout(() => {
          socket.off('room:state:response', onResponse);
          resolve(null);
        }, timeoutMs);

        addSocketListener('room:state:response', onResponse);
        socket.emit('room:state:request', { roomId, requestId, topic: opts?.topic });
      });
    }

    const provideState = <TState = any>(
      provider: (req: RoomStateRequest & { requesterSocketId?: string }) => Promise<TState> | TState,
    ) => {
      const wrapped = async (req: RoomStateRequest & { requesterSocketId?: string }) => {
        if (req.roomId !== roomId) return;
        const payload = await provider(req);
        socket.emit('room:state:response', { roomId, requestId: req.requestId, toSocketId: req.requesterSocketId, payload });
      };
      stateProviders.add(wrapped);
      return () => stateProviders.delete(wrapped);
    };

    const onStateRequest = async (payload: RoomStateRequest & { requesterSocketId: string }) => {
      if (payload.roomId !== roomId) return;
      for (const provider of stateProviders) {
        try {
          await provider(payload);
        } catch {
          // ignore provider errors
        }
      }
    };

    addSocketListener('room:state:request', onStateRequest);

    const leave = async () => {
      if (joined) {
        await this.leaveRoom(roomId, connectOpts, effectiveBase);
        joined = false;
      }
      removeSocketListeners();
      stateProviders.clear();
      messageHandlers.clear();
      typedHandlers.clear();
      presenceHandlers.clear();
      statusHandlers.clear();
    };

    const disconnect = () => {
      leave().catch(() => undefined);
      this.disconnect();
    };

    return {
      roomId,
      status: () => currentStatus,
      socketId: () => socket?.id,
      sendText,
      send,
      onMessage,
      onTyped,
      onPresence,
      onStatus,
      requestState,
      provideState,
      leave,
      disconnect,
      getCursor: () => currentCursor,
      setCursor: cursor => {
        currentCursor = cursor;
      },
    };
  }
}
